import { ChevronRight, Copy, Plus, Layers2, Target } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { ResourceMoniteringContext } from "../../resourcePlanner/resourceMonitoring/ResourceContext";
import moment from "moment";
import { useDispatch } from "react-redux";
import { generalActions } from "../../../store/actions";
import { TaskInputWithDates } from "./InputWithDates";
import { dateOnlyFormatted, isDateGreaterThan } from "../../../utils/systemSettings";
import CustomInput from "../../../components/controls/input/customInput/CustomInput";
import { calculateDurationNoWeekends } from "../utils/date";
import {
  Popover,
  PopoverTrigger,
  PopoverSurface,
  TableCellLayout,
} from "@fluentui/react-components";
import { MeatBallsMenuIcon } from "../../../modules/default-svg";
import Modal from "../../../components/controls/modal/Modal";
import {
  CompletedTaskIcon,
  InProgressTaskIcon,
  SlippingTaskIcon,
  MonitoringTaskIcon,
  OnHoldTaskIcon,
} from "../icons/icons";

export default function Sidebar({
  reference,
  scopes,
  expandedScopes,
  toggleScope,
  expandedTaskGroups,
  toggleTaskGroup,
  showTaskModal,
  taskDates,
  viewType,
  sidebarWidth,
  columnWidths,
  handleTaskModal,
  setScopeItem,
  overviewType,
  isHeaderCollapsed = false,
  isHideTask = false,
  hoveredRowId,
  setHoveredRowId
}) {
  const dispatch = useDispatch();
  const handleTaskPopup = (scope, taskGroup = null) => showTaskModal(scope, true, taskGroup);
  const settings = JSON.parse(localStorage.getItem("settings"));
  const [setting] = useState(settings);
  const [scopeName, setScopeName] = useState("");
  const [scopeStartDate, setScopeStartDate] = useState(null);
  const [scopeEndDate, setScopeEndDate] = useState(null);

  const [taskName, setTaskName] = useState({});
  const [taskProgress, setTaskProgress] = useState({});
  const {
    project,
    taskId,
    setTaskId,
    // fetchCalendarDetails,
    baselineScopes,
    scopeId,
    setScopeId,
    handleCreateScope,
    handleCreateTask,
    handleUpdateTask,
    duplicateScope,
    duplicateTaskGroup,
  } = useContext(ResourceMoniteringContext);
  const [formErrors, setFormErrors] = useState({});
  const [newTaskDates, setNewTaskDates] = useState({});
  const [editingTaskId, setEditingTaskId] = useState(null);
  const [editingTaskName, setEditingTaskName] = useState("");
  const [editingTaskProgress, setEditingTaskProgress] = useState("");
  const [editingTaskDates, setEditingTaskDates] = useState(null);
  const [activeTask, setActiveTask] = useState(null);
  const [activeScope, setActiveScope] = useState(null);
  const [clickTimeout, setClickTimeout] = useState(null);
  const [newTaskFocusInput, setNewTaskFocusInput] = useState(false);
  const [scopePopoverStates, setScopePopoverStates] = useState({});
  const [groupPopoverStates, setGroupPopoverStates] = useState({});
  const [isDuplicatingScope, setIsDuplicatingScope] = useState(false);
  const [duplicateEntityData, setDuplicateEntityData] = useState(null);
  const [isDuplicateScopeModal, setIsDuplicateScopeModal] = useState(false);

  const handleScopePopoverChange = (scopeId, isOpen) => {
    setScopePopoverStates((prev) => ({
      ...prev,
      [scopeId]: isOpen,
    }));
  };

  const handleGroupPopoverChange = (groupId, isOpen) => {
    setGroupPopoverStates((prev) => ({
      ...prev,
      [groupId]: isOpen,
    }));
  };

  const handleScopeDuplicate = (e, scope, isIncludeTasks = false) => {
    e.stopPropagation();
    e.preventDefault();
    handleScopePopoverChange(scope.id, false);
    setDuplicateEntityData({ ...scope, isIncludeTasks, kind: "scope" });
    setIsDuplicateScopeModal(true);
  };

  const handleTaskGroupDuplicate = (e, group, isIncludeTasks = false) => {
    e.stopPropagation();
    e.preventDefault();
    handleGroupPopoverChange(group.id, false);
    setDuplicateEntityData({ ...group, isIncludeTasks, kind: "taskGroup" });
    setIsDuplicateScopeModal(true);
  };

  const handleConfirmDuplicate = () => {
    if (!duplicateEntityData) return;
    setIsDuplicatingScope(true);
    const duplicateFn =
      duplicateEntityData.kind === "taskGroup" ? duplicateTaskGroup : duplicateScope;
    duplicateFn({
      id: duplicateEntityData.id,
      payload: { IsIncludeTasks: duplicateEntityData.isIncludeTasks },
    });

    setTimeout(() => {
      setIsDuplicatingScope(false);
      setIsDuplicateScopeModal(false);
      setDuplicateEntityData(null);
    }, 1000);
  };

  useEffect(() => {
    if (scopes.length > 0) {
      setNewTaskDates(
        scopes.reduce((acc, scope) => {
          acc[scope.id] = {
            startDate: "",
            endDate: "",
          };
          return acc;
        }, {})
      );
    }
  }, [scopes]);

  useEffect(() => {
    if (Object.keys(formErrors).length > 0) {
      const timer = setTimeout(() => {
        setFormErrors({});
      }, 3000);

      return () => clearTimeout(timer);
    }
  }, [formErrors]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const isTaskDiv =
        e.target.classList.contains("task-div") ||
        e.target.parentElement?.classList.contains("task-div");

      const isScopeNameElement =
        e.target.classList.contains("scope-name-element") ||
        e.target.parentElement?.classList.contains("scope-name-element");

      if (isTaskDiv || isScopeNameElement) {
        return;
      }

      if (taskId || scopeId) {
        setTaskId(() => null);
        setActiveTask(() => null);
        setScopeId(() => null);
        setActiveScope(() => null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setTaskId(() => null);
    setScopeId(() => null);
    setActiveTask(() => null);
    setActiveScope(() => null);
  }, []);

  // useEffect(() => {
  //   if (scopeId || taskId) {
  //     fetchCalendarDetails(true);
  //   }
  // }, [taskId, scopeId]);

  const statusIconMap = {
    0: <InProgressTaskIcon style={{minWidth: '12px'}}/>,
    1: <MonitoringTaskIcon style={{minWidth: '12px'}}/>,
    2: <SlippingTaskIcon style={{minWidth: '12px'}}/>,
    3: <CompletedTaskIcon style={{minWidth: '12px'}}/>,
    4: <OnHoldTaskIcon style={{minWidth: '12px'}}/>
  }
  const handleScopeSave = async () => {
    if (!scopeName) {
      dispatch(
        generalActions.showToast({
          title: "Scope name is required",
          type: "error",
        })
      );
    }

    if (
      scopeStartDate &&
      scopeEndDate &&
      isDateGreaterThan(scopeStartDate, scopeEndDate)
    ) {
      dispatch(
        generalActions.showToast({
          title: "End date must be greater than start date.",
          type: "error",
        })
      );
    }

    const payload = {
      Name: scopeName,
      ProjectId: project.id,
      StartDate: scopeStartDate,
      EndDate: scopeEndDate,
      RevisedEndDate: null,
      Priority: 0,
      Budget: 0,
      Status: 0,
    };
    handleCreateScope(payload);
    clearScopeData();
  };

  const clearScopeData = () => {
    setScopeName('');
    setScopeStartDate(null);
    setScopeEndDate(null);
  }
  const handleTaskSave = async (scopeId) => {
    const errors = {};

    if (!taskName[scopeId]) {
      errors[scopeId] = `Please enter task name`;
      setFormErrors(errors);
      dispatch(
        generalActions.showToast({
          title: "Please enter task name",
          type: "error",
        })
      );
      return;
    }

    const { startDate, endDate } = newTaskDates[scopeId] || {};

      if (startDate && endDate && isDateGreaterThan(startDate, endDate)) {
        errors[
          "endDate"
        ] = `End date must be greater than or equal to start date.`;
        setFormErrors(errors);
        dispatch(
          generalActions.showToast({
            title: "End date must be greater than or equal to start date",
            type: "error",
          })
        );
        return;
      }

    const payload = {
      Name: taskName[scopeId],
      progress: taskProgress[scopeId],
      startDate: startDate || null,
      endDate: endDate || null,
      ScopeId: scopeId,
      ProjectId: project.id,
      status: 0,
      Priority: 0,
    };
    handleCreateTask(payload);
    handleTaskNameChange(scopeId,'');
    handleTaskProgressChange(scopeId,0);
    setNewTaskDates((prevState) => ({
      ...prevState,
      [scopeId]: {
        ...prevState[scopeId],
        startDate: null,
        endDate: null,
      },
    }))
    setNewTaskFocusInput(true);
  };

  const handleTaskNameChange = (scopeId, value) => {
    setTaskName((prev) => ({
      ...prev,
      [scopeId]: value,
    }));
  };

  const handleTaskProgressChange = (scopeId, value) => {
    setTaskProgress((prev) => ({
      ...prev,
      [scopeId]: value,
    }));
  };

  const handleTaskEdit = (task) => {
    if (editingTaskId === task.id) {
      return;
    }
    setEditingTaskId(() => task.id);
    setEditingTaskName(() => task.name);
    setEditingTaskProgress(() => task.progress);
    setEditingTaskDates(() => ({
      startDate: taskDates[task.id].startDate,
      endDate: taskDates[task.id].endDate,
    }));
  };

  const handleTaskEditCancel = () => {
    setEditingTaskId(null);
    setEditingTaskName("");
    setEditingTaskProgress(0);
    setEditingTaskDates(() => ({
      startDate: null ,
      endDate: null,
    }));
  };

  const handleTaskCreateCancel = (scopeId) => {
    handleTaskNameChange(scopeId,'');
    handleTaskProgressChange(scopeId,0);
    setNewTaskDates((prevState) => ({
      ...prevState,
      [scopeId]: {
        ...prevState[scopeId],
        startDate: null,
        endDate: null,
      },
    }))
  };

  const updateTask = async (scopeId) => {
    const payload = {
      Name: editingTaskName,
      Progress: editingTaskProgress,
      AssignedScopes: [scopeId],
      startDate: editingTaskDates.startDate,
      endDate: editingTaskDates.endDate,
    };
    handleUpdateTask(editingTaskId, payload);
    setEditingTaskId(null);
  };

  const handleScopeClick = (scopeId) => {
    setTaskId(() => null);
    setActiveTask(() => null);
    setScopeId(() => scopeId);
    setActiveScope(() => scopeId);
  };

  const handleTaskClick = (taskId) => {
    setScopeId(() => null);
    setActiveScope(() => null);
    setActiveTask(() => taskId);
    setTaskId(() => taskId);
  };

  const handleSingleClick = (task) => {
    handleTaskModal(task);
  };
  const handleDoubleClick = (task) => {
    handleTaskClick(task.id);
    handleTaskEdit(task);
  };

  const handleClick = (e, task) => {
    e.preventDefault();
    if (clickTimeout) {
      clearTimeout(clickTimeout);
      setClickTimeout(null);
      handleDoubleClick(task);
    } else {
      setClickTimeout(
        setTimeout(() => {
          handleSingleClick(task);
          setClickTimeout(null);
        }, 300)
      );
    }
  };

  return (
    <div
      ref={reference}
      className="timeline-sidebar-wrapper overflow-y-auto overflow-x-scroll w-full"
      style={{
        height: `${viewType === "daily"
          ? `calc(100vh - ${overviewType === "Project" ?  (isHeaderCollapsed ? 305 : 506) : 246}px)`
          : `calc(100vh - ${overviewType === "Project" ? (isHeaderCollapsed ? 327 : 531)  : 271}px)`
          }`,
        minWidth: sidebarWidth,
      }}
    >
      {scopes.map((scope, index) => (
        <div
          key={scope.id}
          className={`${index % 2 === 0 ? "bg-white" : "bg-[#f5f5f5]"} border-b`}
        >
          {/* SCOPES */}

          <div
            onMouseEnter={() => setHoveredRowId(scope.id)}
            onMouseLeave={() => setHoveredRowId(null)}
            className={`timeline-row py-1 ${baselineScopes?.length > 0 ? "h-[100px]" : "h-[52px]"} ${activeScope === scope.id ? "!bg-[#EDECF8]" : ""} flex items-center ${hoveredRowId === scope.id && "!bg-[#ece7fd]"}`}
          >
            <div
              className="flex px-2 justify-start gap-1 items-center"
              style={{
                width: columnWidths.scopes - 20,
              }}
            >
              <button
                onClick={() => toggleScope(scope.id)}
                className="transition-transform duration-200"
              >
                <ChevronRight
                  size={16}
                  className={`transform ${expandedScopes[scope.id] ? "rotate-90" : ""
                    }`}
                />
              </button>
              <span
                className="font-bold truncate block cursor-pointer scope-name-element capitalize"
                /*onClick={() => {
                  handleScopeClick(scope.id);
                  setScopeItem(scope);
                }}*/
              >
                {scope.name}
              </span>
              <div className="timeline-meatballs-trigger">
                <Popover
                  open={scopePopoverStates[scope.id]}
                  onOpenChange={(isOpen) =>
                    handleScopePopoverChange(scope.id, isOpen)
                  }
                  positioning="below-start"
                >
                  <PopoverTrigger disableButtonEnhancement>
                    <TableCellLayout
                      media={<MeatBallsMenuIcon />}
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleScopePopoverChange(
                          scope.id,
                          !scopePopoverStates[scope.id]
                        );
                      }}
                    />
                  </PopoverTrigger>
                  <PopoverSurface
                    tabIndex={-1}
                    className="listingPopoverWrapper"
                  >
                    <div className="listingPopoverContent">
                      <div
                        className="popItem"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          handleScopePopoverChange(scope.id, false);
                          handleTaskPopup(scope);
                        }}
                      >
                        <Plus size={16} color="#342c82"/>
                        <p className="text-sm-400 ms-12 text-color-primary">Add Task</p>
                      </div>
                      {scope.tasks && scope.tasks.length === 0 ? (
                        <div
                          className="popItem"
                          onClick={(e) => handleScopeDuplicate(e, scope)}
                        >
                          <Copy size={16} color="#342c82"/>
                          <p className="text-sm-400 ms-12 text-color-primary">Duplicate</p>
                        </div>
                      ) : (
                        <Popover positioning="after-top" trapFocus>
                          <PopoverTrigger disableButtonEnhancement>
                            <div className="popItem">
                              <Copy size={16} color="#342c82" />
                              <p className="text-sm-400 ms-12 text-color-primary">Duplicate</p>
                            </div>
                          </PopoverTrigger>
                          <PopoverSurface className="listingPopoverWrapper">
                            <div className="listingPopoverContent">
                              <div
                                className="popItem"
                                onClick={(e) => handleScopeDuplicate(e, scope)}
                              >
                                <Target size={16} color="#342c82"/>
                                <p className="text-sm-400 ms-12 text-color-primary">Scope</p>
                              </div>
                              <div
                                className="popItem"
                                onClick={(e) => handleScopeDuplicate(e, scope, true)}
                              >
                                <Layers2 size={16} color="#342c82"/>
                                <p className="text-sm-400 ms-12 text-color-primary">Scope & tasks</p>
                              </div>
                            </div>
                          </PopoverSurface>
                        </Popover>
                      )}
                    </div>
                  </PopoverSurface>
                </Popover>
              </div>
            </div>
            <p
              className="text-s font-medium text-center"
              style={{
                minWidth: columnWidths.completed,
              }}
            >
              {scope?.progress}%
            </p>
            <p
              className="text-s font-medium text-center truncate"
              style={{
                minWidth: columnWidths.duration,
              }}
            >
              {`${calculateDurationNoWeekends(scope?.startDate, scope?.endDate)} days`}
            </p>
            <p
              className="text-s px-2 font-medium text-center truncate"
              style={{
                minWidth: columnWidths.startDate,
              }}
            >
              {scope?.startDate
                ? dateOnlyFormatted(setting, scope.startDate)
                : ""}
            </p>
            <p
              className="text-s px-2 font-medium text-center truncate"
              style={{
                minWidth: columnWidths.endDate,
              }}
            >
              {scope?.endDate
                ? dateOnlyFormatted(setting, scope.endDate)
                : ""}
            </p>
          </div>

          {/* <div className="flex flex-row items-center gap-2">
                <div className="ml-6 w-3/5 bg-gray-200 h-[4px] rounded-full">
                  <div
                    className="bg-[#342C82] h-full rounded-full"
                    style={{ width: `${scope.completion}%` }}
                  />
                </div>
                <span className="text-xs">{scope.completion}%</span>
              </div> */}
          {expandedScopes[scope.id] && (
            <div className="flex flex-col gap-2">
              {(() => {
                const renderTaskRow = (task, indentClass) => (
                  <div className="w-full" key={task.id}>
                    <div
                      onMouseEnter={() => setHoveredRowId(task.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                      className={`cursor-pointer ${hoveredRowId === task.id && "bg-[#ece7fd]"}`}
                    >
                      {editingTaskId === task.id ? (
                        <TaskInputWithDates
                          scope={scope}
                          index={index}
                          taskName={editingTaskName}
                          taskProgress={editingTaskProgress}
                          handleTaskNameChange={(_, value) => {
                            setEditingTaskName(() => value);
                          }}
                          handleTaskProgressChange={(_, value) => {
                            setEditingTaskProgress(() => value);
                          }}
                          handler={updateTask}
                          newTaskDates={editingTaskDates}
                          setNewTaskDates={setEditingTaskDates}
                          isEditing={true}
                          columnWidths={columnWidths}
                          onCancel={handleTaskEditCancel}
                          isHideTask={isHideTask}
                        />
                      ) : (
                        <div
                          className={`${activeTask === task.id ? "!bg-[#EDECF8]" : ""
                            } h-[20px] cursor-pointer flex flex-row items-center task-div justify-between`}
                          onClick={(e) => handleClick(e, task)}
                        >
                          <div className="flex items-center w-full">
                            <div
                              className={`flex items-center gap-2 ${indentClass}`}
                              style={{
                                width: columnWidths.scopes - 20,
                              }}
                            >
                              {statusIconMap[task.status]}
                              <span className={`text-s font-medium truncate`}>
                                {task.name}
                              </span>
                            </div>
                            <p
                              className="text-s font-medium text-center truncate"
                              style={{
                                minWidth: columnWidths.completed,
                              }}
                            >
                              {task.progress || 0}%
                            </p>
                            <p
                              className="text-s font-medium text-center truncate"
                              style={{
                                minWidth: columnWidths.duration,
                              }}
                            >
                              {`${calculateDurationNoWeekends(taskDates?.[task.id]?.startDate, taskDates?.[task.id]?.endDate)} days`}
                            </p>
                            <p
                              className="text-s font-medium text-center truncate"
                              style={{
                                minWidth: columnWidths.startDate,
                              }}
                            >
                              {taskDates?.[task.id]?.startDate
                                ? dateOnlyFormatted(
                                    setting,
                                    taskDates[task.id].startDate
                                  )
                                : ""}
                            </p>
                            <p
                              className="text-s font-medium text-center truncate"
                              style={{
                                minWidth: columnWidths.endDate,
                              }}
                            >
                              {taskDates?.[task.id]?.endDate
                                ? dateOnlyFormatted(
                                    setting,
                                    taskDates[task.id].endDate
                                  )
                                : ""}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                    {baselineScopes && baselineScopes.length > 0 && (
                      <div>
                        <div className="h-[20px]"></div>
                      </div>
                    )}
                  </div>
                );

                const renderTaskGroupRow = (group) => (
                  <div key={group.id}>
                    <div
                      onMouseEnter={() => setHoveredRowId(group.id)}
                      onMouseLeave={() => setHoveredRowId(null)}
                      className={`timeline-row py-1 h-[52px] flex items-center ${hoveredRowId === group.id && "!bg-[#ece7fd]"}`}
                    >
                      <div
                        className="flex justify-start gap-1 items-center"
                        style={{
                          width: columnWidths.scopes - 20,
                          paddingLeft: '20px'
                        }}
                      >
                        <button
                          onClick={() => toggleTaskGroup(group.id)}
                          className="transition-transform duration-200"
                        >
                          <ChevronRight
                            size={16}
                            className={`transform ${expandedTaskGroups[group.id] ? "rotate-90" : ""
                              }`}
                          />
                        </button>
                        <span className="font-bold truncate block cursor-pointer scope-name-element capitalize">
                          {group.name}
                        </span>
                        <div className="timeline-meatballs-trigger">
                          <Popover
                            open={groupPopoverStates[group.id]}
                            onOpenChange={(isOpen) =>
                              handleGroupPopoverChange(group.id, isOpen)
                            }
                            positioning="below-start"
                          >
                            <PopoverTrigger disableButtonEnhancement>
                              <TableCellLayout
                                media={<MeatBallsMenuIcon />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleGroupPopoverChange(
                                    group.id,
                                    !groupPopoverStates[group.id]
                                  );
                                }}
                              />
                            </PopoverTrigger>
                            <PopoverSurface
                              tabIndex={-1}
                              className="listingPopoverWrapper"
                            >
                              <div className="listingPopoverContent">
                                <div
                                  className="popItem"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    handleGroupPopoverChange(group.id, false);
                                    handleTaskPopup(scope, group);
                                  }}
                                >
                                  <Plus size={16} color="#342c82"/>
                                  <p className="text-sm-400 ms-12 text-color-primary">Add Task</p>
                                </div>
                                {group.tasks && group.tasks.length === 0 ? (
                                  <div
                                    className="popItem"
                                    onClick={(e) => handleTaskGroupDuplicate(e, group)}
                                  >
                                    <Copy size={16} color="#342c82"/>
                                    <p className="text-sm-400 ms-12 text-color-primary">Duplicate</p>
                                  </div>
                                ) : (
                                  <Popover positioning="after-top" trapFocus>
                                    <PopoverTrigger disableButtonEnhancement>
                                      <div className="popItem">
                                        <Copy size={16} color="#342c82" />
                                        <p className="text-sm-400 ms-12 text-color-primary">Duplicate</p>
                                      </div>
                                    </PopoverTrigger>
                                    <PopoverSurface className="listingPopoverWrapper">
                                      <div className="listingPopoverContent">
                                        <div
                                          className="popItem"
                                          onClick={(e) => handleTaskGroupDuplicate(e, group)}
                                        >
                                          <Target size={16} color="#342c82"/>
                                          <p className="text-sm-400 ms-12 text-color-primary">Task Group</p>
                                        </div>
                                        <div
                                          className="popItem"
                                          onClick={(e) => handleTaskGroupDuplicate(e, group, true)}
                                        >
                                          <Layers2 size={16} color="#342c82"/>
                                          <p className="text-sm-400 ms-12 text-color-primary">Task Group & tasks</p>
                                        </div>
                                      </div>
                                    </PopoverSurface>
                                  </Popover>
                                )}
                              </div>
                            </PopoverSurface>
                          </Popover>
                        </div>
                      </div>
                      <p
                        className="text-s font-medium text-center"
                        style={{
                          minWidth: columnWidths.completed,
                        }}
                      >
                        {group?.progress}%
                      </p>
                      <p
                        className="text-s font-medium text-center truncate"
                        style={{
                          minWidth: columnWidths.duration,
                        }}
                      >
                        {`${calculateDurationNoWeekends(group?.startDate, group?.endDate)} days`}
                      </p>
                      <p
                        className="text-s px-2 font-medium text-center truncate"
                        style={{
                          minWidth: columnWidths.startDate,
                        }}
                      >
                        {group?.startDate
                          ? dateOnlyFormatted(setting, group.startDate)
                          : ""}
                      </p>
                      <p
                        className="text-s px-2 font-medium text-center truncate"
                        style={{
                          minWidth: columnWidths.endDate,
                        }}
                      >
                        {group?.endDate
                          ? dateOnlyFormatted(setting, group.endDate)
                          : ""}
                      </p>
                    </div>
                    {expandedTaskGroups[group.id] &&
                      group.tasks?.map((task) => renderTaskRow(task, "pl-14"))}
                  </div>
                );

                return (
                  <>
                    {(scope.taskGroups || []).map((group) => renderTaskGroupRow(group))}
                    {scope.tasks.map((task) => renderTaskRow(task, "pl-8"))}
                  </>
                );
              })()}
              <TaskInputWithDates
                scope={scope}
                index={index}
                taskName={taskName}
                taskProgress={taskProgress}
                handleTaskNameChange={handleTaskNameChange}
                handleTaskProgressChange={handleTaskProgressChange}
                handler={handleTaskSave}
                newTaskDates={newTaskDates}
                setNewTaskDates={setNewTaskDates}
                sidebarWidth={sidebarWidth}
                columnWidths={columnWidths}
                isNew={true}
                autoFocusTask={newTaskFocusInput}
                onCancel={handleTaskCreateCancel}
              />
            </div>
          )}
        </div>
      ))}
      <div className="relative flex w-full h-[30px]">
        <input
          type="text"
          className={`h-[30px] w-full pl-4 outline-none text-s `}
          placeholder={
            formErrors.scopeName?.length > 0
              ? formErrors.scopeName
              : "new scope"
          }
          style={{
            width: columnWidths.scopes -21 ,
          }}
          value={scopeName}
          onChange={(event) => setScopeName(event.target.value)}
          onKeyUp={(event) => {
            if (event.key === "Enter") {
              handleScopeSave();
            }
            else if (event.key === "Escape") {
              event.preventDefault();
              event.stopPropagation();
              clearScopeData();
            }
          }}
        />
        <p
          className="h-[30px]"
          style={{
            minWidth: columnWidths.completed,
          }}
        />
        <p
          className="h-[30px]"
          style={{
            minWidth: columnWidths.duration,
          }}
        />
        <div
          className="h-[30px] flex items-center justify-center"
          style={{
            minWidth: columnWidths.startDate,
          }}
        >
          <CustomInput
            type="date"
            width="80px"
            height="18px"
            DateStyleClass="date-input DatePickerInput"
            DateStyles={{ fontSize: "10px" }}
            handleChange={(value) => setScopeStartDate(value)}
            value={scopeStartDate}
            onKeyUp={(event) => {
              if (event.key === "Enter") {
                handleScopeSave();
              }
            }}
          />
        </div>
        <div
          className="h-[30px] flex items-center justify-center"
          style={{
            minWidth: columnWidths.endDate,
          }}
        >
          <CustomInput
            type="date"
            width="80px"
            height="18px"
            DateStyleClass="date-input DatePickerInput"
            DateStyles={{ fontSize: "10px" }}
            minDate={scopeStartDate}
            handleChange={(value) => setScopeEndDate(value)}
            value={scopeEndDate}
            onKeyUp={(event) => {
              if (event.key === "Enter") {
                handleScopeSave();
              }
            }}
          />
        </div>
      </div>

      {/* Duplicate Confirmation Modal */}
      {isDuplicateScopeModal && (
        <Modal
          title="Duplication Confirmation"
          body={
            <p className="text-sm-400 subHeading-color">
              {`Are you sure you want to duplicate ${duplicateEntityData.name}?`}
            </p>
          }
          handleCancel={() => {
            setIsDuplicateScopeModal(false);
            setDuplicateEntityData(null);
          }}
          handleSave={handleConfirmDuplicate}
          saveBtnLabel="Confirm"
          cancelBtnLabel="Cancel"
          isLoading={isDuplicatingScope}
        />
      )}
    </div>
  );
}
